import { Copy, Mail } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { writeTextToClipboard } from '@/lib/clipboard';

import { usePaymentLink } from './useAccounts';

interface PaymentLinkDialogProps {
  onClose: () => void;
  ownerPath: string;
  name: string;
  recipient: string;
}

export function PaymentLinkDialog({
  onClose,
  ownerPath,
  name,
  recipient,
}: PaymentLinkDialogProps) {
  const [url, setUrl] = useState('');
  const paymentLink = usePaymentLink(ownerPath);
  const toast = useToast();

  const createLink = async (email: boolean) => {
    try {
      const link = await paymentLink.mutateAsync(email);
      setUrl(link.url);
      if (email) toast.success('Payment link sent', `Emailed to ${recipient}.`);
    } catch (e) {
      toast.error(
        'Could not create the payment link',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send payment link</DialogTitle>
          <DialogDescription>
            The link lets {name} add a payment method on Stripe, so the
            subscription continues after the trial. Each link works for a short
            time.
          </DialogDescription>
        </DialogHeader>
        {url ? (
          <div className="flex gap-2">
            <Input readOnly value={url} />
            <Button
              variant="outline"
              onClick={() =>
                writeTextToClipboard(Promise.resolve(url)).then(() =>
                  toast.success('Link copied')
                )
              }
            >
              <Copy className="mr-1 h-4 w-4" />
              Copy
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => createLink(false)}
            disabled={paymentLink.isPending}
          >
            Create link
          </Button>
        )}
        <DialogFooter>
          <Button
            onClick={() => createLink(true)}
            disabled={paymentLink.isPending}
          >
            <Mail className="mr-1 h-4 w-4" />
            Email it to {recipient}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
